import { useTranslation } from 'react-i18next';
/**
 * PublicSurveyForm — what the customer sees at /survey/{token}.
 *
 * Unauthenticated route — only the token guards access. Renders the
 * survey template's sections + questions, captures answers, then
 * collects a typed name + drawn signature on a canvas.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicSurveyForm() {
  const { t } = useTranslation();
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [answers, setAnswers] = useState({}); // key = sectionKey.questionKey
  const [signedName, setSignedName] = useState('');
  const [signedTitle, setSignedTitle] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  useEffect(() => {
    axios.get(`${API}/public/customer-survey/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || 'Could not load survey'));
  }, [token]);

  // Signature canvas — touch + mouse, minimal drawing logic. Stores
  // the captured PNG as a data URL when submitting.
  const startDraw = (e) => {
    drawing.current = true;
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    const { x, y } = getXY(e);
    ctx.moveTo(x, y);
  };
  const moveDraw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getXY(e);
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#1e3a8a';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
  };
  const stopDraw = () => { drawing.current = false; };
  const getXY = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };
  const clearCanvas = () => {
    const c = canvasRef.current;
    c.getContext('2d').clearRect(0, 0, c.width, c.height);
  };
  const isCanvasEmpty = () => {
    const c = canvasRef.current;
    if (!c) return true;
    const px = c.getContext('2d').getImageData(0, 0, c.width, c.height).data;
    for (let i = 3; i < px.length; i += 4) if (px[i] !== 0) return false;
    return true;
  };

  const setAnswer = (sectionKey, questionKey, field, value) => {
    setAnswers((prev) => ({
      ...prev,
      [`${sectionKey}.${questionKey}`]: {
        ...(prev[`${sectionKey}.${questionKey}`] || {}),
        section_key: sectionKey,
        question_key: questionKey,
        [field]: value,
      },
    }));
  };

  const submit = async () => {
    if (!signedName.trim()) { toast.error(t('auto.misc.pleaseTypeYourNameToSign')); return; }
    if (isCanvasEmpty()) { toast.error(t('auto.misc.pleaseDrawYourSignature')); return; }
    // Validate required questions
    const missing = [];
    (data.template.sections || []).forEach((sec) => {
      sec.questions.forEach((q) => {
        if (!q.required) return;
        const a = answers[`${sec.key}.${q.key}`];
        if (!a) { missing.push(`${sec.label}: ${q.label}`); return; }
        if (q.type === 'rating' && !a.rating) missing.push(`${sec.label}: ${q.label}`);
        if (q.type === 'yes_no' && a.yes_no === undefined) missing.push(`${sec.label}: ${q.label}`);
        if (q.type === 'text' && !a.text) missing.push(`${sec.label}: ${q.label}`);
      });
    });
    if (missing.length) {
      toast.error(`Please answer: ${missing.slice(0, 2).join(' / ')}${missing.length > 2 ? ' (+ more)' : ''}`);
      return;
    }
    setSubmitting(true);
    try {
      const sig = canvasRef.current.toDataURL('image/png');
      await axios.post(`${API}/public/customer-survey/${token}/submit`, {
        answers: Object.values(answers),
        signed_name: signedName.trim(),
        signed_title: signedTitle.trim() || null,
        signature_image: sig,
      });
      setSubmitted(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submission failed');
    } finally { setSubmitting(false); }
  };

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="max-w-md text-center bg-white rounded shadow p-6">
        <h1 className="text-lg font-semibold text-red-600">{t('auto.misc.surveyUnavailable')}</h1>
        <p className="text-sm text-slate-600 mt-2">{error}</p>
      </div>
    </div>
  );
  if (!data) return <div className="p-12 text-center text-slate-500">{t('auto.misc.loadingSurvey')}</div>;
  if (submitted || data.already_signed) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="max-w-md text-center bg-white rounded shadow p-8">
        <div className="text-5xl mb-3">✓</div>
        <h1 className="text-xl font-semibold text-green-700">{t('auto.misc.thankYou')}</h1>
        <p className="text-sm text-slate-600 mt-2">{t('auto.misc.yourSurveyHasBeenSubmittedThe')}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-3xl mx-auto bg-white rounded shadow p-6 sm:p-8">
        <h1 className="text-2xl font-semibold text-slate-800">{data.survey.title}</h1>
        {data.project_name && (
          <p className="text-sm text-slate-500 mt-1">
            Project: <strong>{data.project_name}</strong>
            {data.project_location && ` · ${data.project_location}`}
          </p>
        )}
        {data.survey.scope_description && (
          <p className="text-xs text-slate-500 italic mt-0.5">Scope: {data.survey.scope_description}</p>
        )}
        {data.template?.intro_text && (
          <p className="text-sm text-slate-700 mt-4 p-3 bg-blue-50 border border-blue-100 rounded">{data.template.intro_text}</p>
        )}

        <div className="mt-6 space-y-6">
          {(data.template?.sections || []).map((sec) => (
            <section key={sec.key} data-testid={`survey-section-${sec.key}`}>
              <h2 className="text-lg font-semibold text-slate-800 border-b border-slate-200 pb-1.5 mb-3">{sec.label}</h2>
              {sec.description && <p className="text-xs text-slate-500 mb-3">{sec.description}</p>}
              <div className="space-y-4">
                {sec.questions.map((q) => {
                  const ans = answers[`${sec.key}.${q.key}`] || {};
                  return (
                    <div key={q.key}>
                      <label className="block text-sm text-slate-700 mb-1.5">
                        {q.label}
                        {q.required && <span className="text-red-500 ml-0.5">*</span>}
                      </label>
                      {q.type === 'rating' && (
                        <div className="flex items-center gap-1" data-testid={`q-${sec.key}-${q.key}`}>
                          {[1, 2, 3, 4, 5].map((n) => (
                            <button key={n} type="button"
                              onClick={() => setAnswer(sec.key, q.key, 'rating', n)}
                              className={`w-8 h-8 text-xl ${ans.rating >= n ? 'text-amber-500' : 'text-slate-300'} hover:text-amber-400`}>★</button>
                          ))}
                          {ans.rating && <span className="text-xs text-slate-500 ml-2">{ans.rating}/5</span>}
                        </div>
                      )}
                      {q.type === 'yes_no' && (
                        <div className="flex gap-2" data-testid={`q-${sec.key}-${q.key}`}>
                          {[['Yes', true], ['No', false]].map(([label, val]) => (
                            <button key={label} type="button"
                              onClick={() => setAnswer(sec.key, q.key, 'yes_no', val)}
                              className={`px-3 py-1 text-sm rounded border ${ans.yes_no === val ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}>
                              {label}
                            </button>
                          ))}
                        </div>
                      )}
                      {q.type === 'text' && (
                        <textarea rows={3} value={ans.text || ''}
                          onChange={(e) => setAnswer(sec.key, q.key, 'text', e.target.value)}
                          className="w-full border rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                          placeholder="Your comment…" data-testid={`q-${sec.key}-${q.key}`} />
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        <section className="mt-8 border-t pt-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-3">{t('auto.misc.signOff')}</h2>
          <div className="grid sm:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-sm text-slate-700 mb-1">{t('auto.misc.yourFullName')}</label>
              <input value={signedName} onChange={(e) => setSignedName(e.target.value)}
                className="w-full border rounded px-2 py-1.5 text-sm" data-testid="survey-signed-name" />
            </div>
            <div>
              <label className="block text-sm text-slate-700 mb-1">{t('auto.misc.yourTitleRole')}</label>
              <input value={signedTitle} onChange={(e) => setSignedTitle(e.target.value)}
                className="w-full border rounded px-2 py-1.5 text-sm" data-testid="survey-signed-title" />
            </div>
          </div>
          <div>
            <label className="block text-sm text-slate-700 mb-1">{t('auto.misc.signature')} <span className="text-xs text-slate-400">{t('auto.misc.drawBelow')}</span></label>
            <canvas ref={canvasRef} width={600} height={140}
              className="w-full border-2 border-dashed border-slate-300 rounded bg-white touch-none"
              onMouseDown={startDraw} onMouseMove={moveDraw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
              onTouchStart={startDraw} onTouchMove={moveDraw} onTouchEnd={stopDraw}
              data-testid="survey-signature-canvas" />
            <button type="button" onClick={clearCanvas}
              className="text-xs text-slate-500 hover:text-slate-700 mt-1" data-testid="survey-clear-signature">
              {t('auto.misc.clearSignature')}
            </button>
          </div>
          <button type="button" onClick={submit} disabled={submitting}
            className="mt-4 w-full sm:w-auto px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium disabled:bg-slate-300"
            data-testid="survey-submit-btn">
            {submitting ? t('auto.misc.submitting') : t('auto.misc.submitSurvey')}
          </button>
        </section>
      </div>
    </div>
  );
}
